export interface IBrowserInfo {
  userAgent: string;
  browser: string;
  isIncognito: boolean | null;
  cookiesEnabled: boolean;
  localStorageAvailable: boolean;
  language: string;
  platform: string;
  screen: string;
}

const getBrowserName = (ua: string): string => {
  if (/edg/i.test(ua)) return 'Edge';
  if (/opr|opera/i.test(ua)) return 'Opera';
  if (/chrome|crios/i.test(ua)) return 'Chrome';
  if (/firefox|fxios/i.test(ua)) return 'Firefox';
  if (/safari/i.test(ua)) return 'Safari';
  return 'Unknown';
};

/**
 * Best-effort private/incognito detection. Returns null when it cannot be
 * determined reliably on the current browser.
 */
export const detectIncognito = (): Promise<boolean | null> => {
  return new Promise((resolve) => {
    try {
      const nav = navigator as any;

      // Chromium-based browsers heavily reduce the storage quota in incognito.
      if (nav.storage && typeof nav.storage.estimate === 'function') {
        nav.storage
          .estimate()
          .then((estimate: { quota?: number }) => {
            if (typeof estimate.quota !== 'number') {
              resolve(null);
              return;
            }
            resolve(estimate.quota < 120 * 1024 * 1024);
          })
          .catch(() => resolve(null));
        return;
      }

      // Fallback: IndexedDB is blocked in some private modes (older browsers).
      const request = window.indexedDB.open('coordly_incognito_test');
      request.onerror = () => resolve(true);
      request.onsuccess = () => {
        try {
          window.indexedDB.deleteDatabase('coordly_incognito_test');
        } catch {
          // ignore cleanup failure
        }
        resolve(false);
      };
    } catch {
      resolve(null);
    }
  });
};

const isLocalStorageAvailable = (): boolean => {
  try {
    const key = '__cdl_storage_test__';
    window.localStorage.setItem(key, '1');
    window.localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

export const getBrowserInfo = async (): Promise<IBrowserInfo> => {
  const ua = navigator.userAgent || '';

  return {
    userAgent: ua,
    browser: getBrowserName(ua),
    isIncognito: await detectIncognito(),
    cookiesEnabled: navigator.cookieEnabled,
    localStorageAvailable: isLocalStorageAvailable(),
    language: navigator.language,
    platform: (navigator as any).platform || '',
    screen: `${window.screen?.width}x${window.screen?.height}`,
  };
};
